import { apiRequest } from "api-client";
import { SetStateAction, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export interface DetailPageProps {
  apiEndpoint: string;
  autoI18nFromEnglish?: boolean;
  additionalQueryParams?: object;
  defaultData?: object;
  excludedFields?: string[];
}

export const useDetailPage = ({
  apiEndpoint,
  autoI18nFromEnglish = false,
  additionalQueryParams = {},
  defaultData = {},
  excludedFields = [],
}: DetailPageProps) => {
  const { id } = useParams<{id: string}>();
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const history = useHistory();

  const onSave = () => {
    if (autoI18nFromEnglish) {
      // If there are any missing translations, use English version as default
      const englishTranslation = data?.i18n.find((item: { lang: string; }) => item.lang === 'en');
      data?.i18n.forEach((item: { [x: string]: string; }) => {
        for (const key in item) {
          if (Object.hasOwnProperty.call(item, key)) {
            const trans = item[key];
            if (!trans && englishTranslation[key]) {
              item[key] = englishTranslation[key]
            }
          }
        }
      });
    }

    setSaving(true);
    if (id === "new") {
      apiRequest.post(`/${apiEndpoint}`, data).then((data) => {
        setSaving(false);
        toast.success('Created successfully');
        history.push(history.location.pathname.replace(/new$/, data.id));
      }).catch(err => {
        toast.error(err.message);
        setSaving(false);
      });
    } else {
      apiRequest.put(`/${apiEndpoint}/${id}`, data).then(() => {
        toast.success('Updated successfully');
        setSaving(false);
      }).catch(err => {
        toast.error(err.message);
        setSaving(false);
      });
    }
  };

  const onUpdateData = (newData: SetStateAction<Record<string, any> | null>) => {
    setData({ ...data, ...newData });
  };

  useEffect(() => {
    if (id && id !== "new") {
      setLoading(true);
      apiRequest.get(`/${apiEndpoint}/${id}`, {
        params: additionalQueryParams
      }).then(data => {
        excludedFields.forEach(field => {
          delete data[field];
        });
        setData(data);
        setLoading(false);
      }).catch(err => {
        setLoading(false);
        toast.error(err.message);
      })
    }
    if (id === "new") {
      setData(defaultData);
    }
     // eslint-disable-next-line
  }, [id]);

  return {
    id,
    data,
    loading,
    saving,
    onSave,
    onUpdateData,
  };
};

